import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { getFaqById, createFaq, editFaq } from '../../store/actions/faq/faq.action';
import PageLoader from '../../components/Common/PageLoader';
import CreateFaq from '../../components/Faqs/CreateFaq';

const CreateFaqsContainer = () => {
  // Router hooks
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const categories = useSelector((state) => state.auth.categories);
  const isCreating = useSelector((state) => state.faq?.isCreating || false);

  // Local state
  const [mode, setMode] = useState('CREATE');
  const [faqToBeEdit, setFaqToBeEdit] = useState(null);
  const [accessMode, setAccessMode] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Set access based on categories
  const setAccess = useCallback(() => {
    try {
      const faqCategory = categories.find((category) => category.link === 'faqs');
      if (faqCategory) {
        setAccessMode(faqCategory.mode);
        setAccessDenied(false);
      } else {
        throw new Error('FAQ category not found');
      }
    } catch (err) {
      setAccessDenied(true);
    }
  }, [categories]);

  // Fetch FAQ for edit mode
  const getFaq = useCallback(async (faqId) => {
    try {
      setIsLoading(true);
      const response = await dispatch(getFaqById(faqId));
      setFaqToBeEdit(response);
    } catch (error) {
      console.error('Error fetching FAQ:', error);
      navigate('/faqs');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, navigate]);

  // Handle edit FAQ
  const handleEdit = useCallback(async (faq) => {
    try {
      await dispatch(editFaq(faq));
      // Navigation will be handled by the action
    } catch (error) {
      console.error('Error updating FAQ:', error);
    }
  }, [dispatch]);

  // Handle create FAQ
  const handleCreate = useCallback(async (faqInfo) => {
    try {
      await dispatch(createFaq(faqInfo));
      // Navigation will be handled by the action
    } catch (error) {
      console.error('Error creating FAQ:', error);
    }
  }, [dispatch]);

  // Initialize component on mount
  useEffect(() => {
    // Set mode based on params
    setMode(params.mode === 'edit' ? 'EDIT' : 'CREATE');

    // Set access permissions
    setAccess();

    // Fetch FAQ if ID is present
    if (params.id && params.mode === 'edit') {
      getFaq(params.id);
    }
  }, [params.id, params.mode, getFaq, setAccess]);

  // Update access when categories change
  useEffect(() => {
    setAccess();
  }, [categories, setAccess]);

  // Loading state for edit mode
  if ((mode === 'EDIT' && !faqToBeEdit) || isLoading) {
    return <PageLoader />;
  }

  // Access denied state
  if (accessDenied) {
    return <div>This category is not accessible to the logged in user.</div>;
  }

  return (
    <section>
      <CreateFaq
        accessMode={accessMode}
        onCreate={handleCreate}
        onEdit={handleEdit}
        initialValues={faqToBeEdit}
        mode={mode}
        isCreating={isCreating}
      />
    </section>
  );
};

export default CreateFaqsContainer;
